import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {lazy, Suspense, useMemo, useState} from "react";
import {apiRetrieveImproved} from "../../../api";
import Header from "../../../Header.tsx";
import WhatsWorking from "./-WhatsWorking.tsx";

const Chart = lazy(() => import("react-apexcharts"));

export const Route = createFileRoute('/_app/master/analytics')({
    component: RouteComponent,
})

type AnalyticsData = {
    months: string[];
    days: string[];
    organizers_returned_daily: number[];
    organizers_returned_prev_total: number;
    organizers_created: number[];
    organizers_returned: number[];
    events_setup: number[];
    events_completed: number[];
    players_created: number[];
    players_returned: number[];
};

// '2026-07' -> 'Jul 26'
function monthLabel(m: string): string {
    const [y, mo] = m.split('-').map(Number);
    return new Date(y, (mo || 1) - 1, 1).toLocaleDateString('en-GB', {month: 'short', year: '2-digit'});
}

// '2026-07-31' -> '31 Jul'
function dayLabel(d: string): string {
    const [y, mo, day] = d.split('-').map(Number);
    return new Date(y, (mo || 1) - 1, day || 1).toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
}

// Serves both the monthly cards (pass `months`) and the daily one (pass pre-formatted `labels`).
function BarCard({title, sub, months, labels, values, color, footer}: {
    title: string;
    sub: string;
    months?: string[];
    labels?: string[];
    values: number[];
    color: string;
    footer?: React.ReactNode;
}) {
    const options = useMemo(() => ({
        chart:       {toolbar: {show: false}, zoom: {enabled: false}, background: 'transparent'},
        theme:       {mode: 'dark' as const},
        plotOptions: {bar: {columnWidth: '55%', borderRadius: 4}},
        dataLabels:  {enabled: false},
        legend:      {show: false},
        xaxis:       {
            categories: labels ?? (months || []).map(monthLabel),
            labels:     {style: {colors: '#9ca3af', fontSize: '11px'}},
            axisBorder: {show: false},
            axisTicks:  {show: false},
        },
        yaxis:       {labels: {style: {colors: '#9ca3af'}}, forceNiceScale: true, decimalsInFloat: 0},
        grid:        {show: true, borderColor: '#40405544', strokeDashArray: 3},
        tooltip:     {theme: 'dark' as const, y: {formatter: (v: number) => String(v)}},
    }), [months, labels]);

    const series = useMemo(() => [{name: title, data: values, color}], [title, values, color]);
    const total = values.reduce((s, v) => s + v, 0);

    return (
        <div className="rounded-xl border border-default-200 bg-content1 p-4">
            <div className="flex items-baseline justify-between gap-2">
                <h2 className="font-bold">{title}</h2>
                <span className="text-sm font-bold tabular-nums" style={{color}}>{total}</span>
            </div>
            <p className="mb-2 text-xs text-default-500">{sub}</p>
            {footer}
            <Suspense fallback={<div className="flex h-[240px] items-center justify-center"><Spinner size="sm" /></div>}>
                <Chart options={options as any} series={series} type="bar" height={240} />
            </Suspense>
        </div>
    );
}

function RouteComponent() {
    const [monthsBack, setMonthsBack] = useState(12);
    const [daysBack, setDaysBack] = useState(30);

    const query = useQuery<AnalyticsData>({
        queryKey: ['master-analytics', monthsBack, daysBack],
        queryFn:  () => apiRetrieveImproved('master/analytics' as any, {months: monthsBack, days: daysBack}),
    });

    const d = query.data;

    // Day-over-day movement for organizer returns: this window's total vs the window before it.
    const dailyTotal = (d?.organizers_returned_daily || []).reduce((s, v) => s + v, 0);
    const prevTotal = d?.organizers_returned_prev_total ?? 0;
    const changePct = prevTotal > 0 ? Math.round(((dailyTotal - prevTotal) / prevTotal) * 100) : null;
    const perDay = d?.days?.length ? (dailyTotal / d.days.length) : 0;

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-6xl py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                        <h1 className="mb-1 text-2xl font-bold">Analytics</h1>
                        <p className="mb-4 text-sm text-default-500">
                            Monthly growth and retention. Test accounts and organizer-created placeholder
                            players are excluded; "returned" means coming back after the day/month they signed up.
                        </p>
                    </div>
                    <div className="flex gap-1">
                        {[6, 12, 24].map(n => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setMonthsBack(n)}
                                className={
                                    'rounded-full px-3 py-1 text-sm font-medium transition ' +
                                    (monthsBack === n
                                        ? 'bg-primary text-white'
                                        : 'bg-default-100 text-default-600 hover:bg-default-200')
                                }
                            >
                                {n}m
                            </button>
                        ))}
                    </div>
                </div>

                <WhatsWorking />

                {query.isLoading || !d ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <div className="lg:col-span-2">
                            <BarCard
                                title="Organizers returned by day"
                                sub={`Distinct organizers active on each day (after their signup day), last ${daysBack} days.`}
                                labels={d.days.map(dayLabel)} values={d.organizers_returned_daily} color="#8b5cf6"
                                footer={
                                    <div className="mb-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                                        <span className="text-default-500">
                                            Avg <strong className="text-default-700">{perDay.toFixed(1)}</strong>/day
                                        </span>
                                        <span className="text-default-500">
                                            Previous {daysBack} days: <strong className="text-default-700">{prevTotal}</strong>
                                        </span>
                                        {changePct !== null && (
                                            <span className={changePct >= 0 ? 'font-semibold text-success' : 'font-semibold text-danger'}>
                                                {changePct >= 0 ? '▲' : '▼'} {Math.abs(changePct)}% vs previous period
                                            </span>
                                        )}
                                        <span className="ml-auto flex gap-1">
                                            {[14, 30, 90].map(n => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    onClick={() => setDaysBack(n)}
                                                    className={
                                                        'rounded-full px-2 py-0.5 text-xs font-medium transition ' +
                                                        (daysBack === n
                                                            ? 'bg-primary text-white'
                                                            : 'bg-default-100 text-default-600 hover:bg-default-200')
                                                    }
                                                >
                                                    {n}d
                                                </button>
                                            ))}
                                        </span>
                                    </div>
                                }
                            />
                        </div>
                        <BarCard
                            title="Organizers created"
                            sub="New accounts with the Organizer role, by signup month."
                            months={d.months} values={d.organizers_created} color="#3b82f6"
                        />
                        <BarCard
                            title="Organizers returned"
                            sub="Organizers who logged back in on a day after they signed up (history before login tracking uses each user's last recorded login plus their event/scoring actions)."
                            months={d.months} values={d.organizers_returned} color="#8b5cf6"
                        />
                        <BarCard
                            title="Events set up"
                            sub="Tournaments + leagues created, by creation month."
                            months={d.months} values={d.events_setup} color="#22c55e"
                        />
                        <BarCard
                            title="Events completed"
                            sub="Events where every game is finished, counted in the month the last game finished."
                            months={d.months} values={d.events_completed} color="#f59e0b"
                        />
                        <BarCard
                            title="Players signed up"
                            sub="New self-registered Player accounts (placeholder accounts excluded), by signup month."
                            months={d.months} values={d.players_created} color="#06b6d4"
                        />
                        <BarCard
                            title="Players returned"
                            sub="Players who played a finished game or applied to an event in a month after their signup month."
                            months={d.months} values={d.players_returned} color="#ec4899"
                        />
                    </div>
                )}
            </div>
        </>
    );
}
